import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [status, setStatus] = useState("checking");
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const maxAttempts = 12;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        // Solo consideramos éxito si Stripe confirmó el cobro. NUNCA aceptamos
        // status='complete' por sí solo — eso significa "usuario completó el
        // formulario" pero el pago puede seguir pendiente/fallar (ej: Apple Pay
        // async). Aceptamos 'no_payment_required' porque cubre trials gratuitos
        // futuros si se reactivan.
        const isSuccess = data.payment_status === "paid"
          || data.payment_status === "no_payment_required";
        if (isSuccess) {
          setStatus("success");
          await refreshUser();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("error"); return;
        }
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus("error");
      } catch {
        attempts++;
        if (attempts < maxAttempts) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md text-center animate-fade-in">
        {status === "checking" && (
          <>
            <Loader size={48} className="text-[#E48D57] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl text-[#5B3E2E] mb-2">Verificando pago...</h1>
            <p className="text-sm text-[#5B3E2E]/60">Estamos confirmando tu suscripción</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#6B9070]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-[#6B9070]" />
            </div>
            <h1 className="font-heading text-2xl text-[#5B3E2E] mb-2">Bienvenido a Refugium</h1>
            <p className="text-sm text-[#5B3E2E]/60 mb-8">Tu suscripción está activa. ¡Nos alegra tenerte aquí!</p>
            <Button
              data-testid="enter-app-btn"
              onClick={() => navigate("/app/feed")}
              className="bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full px-8 py-3 font-medium"
            >
              Entrar a Refugium
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-heading text-2xl text-[#5B3E2E] mb-2">Hubo un problema</h1>
            <p className="text-sm text-[#5B3E2E]/60 mb-8">No pudimos confirmar el pago. Intentalo de nuevo.</p>
            <Button
              data-testid="retry-payment-btn"
              onClick={() => navigate("/payment")}
              className="bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full px-8 py-3"
            >
              Volver a intentar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
